import { useMemo } from 'react';

import type { V1CodeManifest } from '@/generated/hooks/grimoire/models';
import { useListCollections } from '@/generated/hooks/bishop/collections/v1/api';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { parseResourceName } from '@/shared/utils';

import { normalizeCollectionSlug } from './rail-utils';

/** How many workspace collections to read when resolving the manifest's slugs.
 *  One page covers every workspace we have seen; a slug past it just renders
 *  without a link rather than breaking the section. */
const COLLECTION_LOOKUP_PAGE_SIZE = 200;

/** What the rail's Library block renders. */
export interface IRailLibrary {
  assetUris: string[];
  collectionSlugs: string[];
  collectionBySlug: ReadonlyMap<string, { id: string; displayName: string }>;
}

/**
 * The Library block's data, read by the always-mounted rail.
 *
 * @param manifest - The draft's code manifest
 * @returns The Library block's data
 */
export function useRailLibrary(manifest: V1CodeManifest): IRailLibrary {
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  // The Library block covers both kinds of library resource a step can reach:
  // assets, recorded as the raw `bishop://` uri, and first-class collections,
  // recorded as the slug written in the automation code.
  const { assetUris, collectionSlugs } = useMemo(() => {
    const uris = new Set<string>();
    const slugs = new Set<string>();
    for (const step of manifest.steps ?? []) {
      for (const asset of step.audit?.assets ?? []) {
        if (asset.uri) uris.add(asset.uri);
      }
      for (const collection of step.audit?.collections ?? []) {
        if (collection.slug_name) slugs.add(collection.slug_name);
      }
    }
    return { assetUris: [...uris], collectionSlugs: [...slugs] };
  }, [manifest.steps]);

  // The manifest names a collection by its code slug, but the collection page
  // is addressed by id, so the workspace's collections are listed once and
  // matched on display name. A slug with no match still renders — it just
  // isn't a link.
  const { data: collectionsData } = useListCollections(
    organizationId,
    workspaceId,
    { page_size: COLLECTION_LOOKUP_PAGE_SIZE },
    {
      query: {
        enabled: Boolean(
          organizationId && workspaceId && collectionSlugs.length > 0,
        ),
      },
    },
  );

  const collectionBySlug = useMemo(() => {
    const bySlug = new Map<string, { id: string; displayName: string }>();
    for (const collection of collectionsData?.data.collections ?? []) {
      const id = parseResourceName(collection.name).collection;
      if (!id || !collection.display_name) continue;
      bySlug.set(normalizeCollectionSlug(collection.display_name), {
        id,
        displayName: collection.display_name,
      });
    }
    return bySlug;
  }, [collectionsData]);

  return { assetUris, collectionSlugs, collectionBySlug };
}
